import { useCallback } from 'react';
import { mainApi, getirApi, migrosApi, yemeksepetiApi } from '../../../../config/apiClient';
import NotificationService from '../../../../services/NotificationService';
import { PaymentType } from '../types';
import type { TrendyolUpdateResult } from './useTrendyolYemek';

export interface UseCourierHandlersDeps {
  selectedOrderId: number | null;
  selectedOrder: any;
  companyId: string | null;
  screenData: any;
  trendyolOrders: any[];
  setTrendyolOrders: React.Dispatch<React.SetStateAction<any[]>>;
  updateTrendyolOrderStatus: (packageId: string, action: 'accept' | 'ready' | 'shipped' | 'delivered') => Promise<TrendyolUpdateResult>;
  getirOrders: any[];
  setGetirOrders: React.Dispatch<React.SetStateAction<any[]>>;
  setMigrosOrders: React.Dispatch<React.SetStateAction<any[]>>;
  refreshGetirToken: () => void;
  fetchGetirOrders: () => Promise<void>;
  fetchYemeksepetiOrders: () => Promise<void>;
  handleUpdateOrderStatus: (orderId: number, newStatus: string, courierFirebaseUid?: string, courierName?: string) => Promise<void>;
  updateGetirOrderStatus: any;
  setSnackbar: (data: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void;
  setSelectCourierModalOpen: (open: boolean) => void;
}

export const useCourierHandlers = (deps: UseCourierHandlersDeps) => {
  const {
    selectedOrderId,
    selectedOrder,
    companyId,
    screenData,
    trendyolOrders,
    setTrendyolOrders,
    updateTrendyolOrderStatus,
    getirOrders,
    setGetirOrders,
    setMigrosOrders,
    refreshGetirToken,
    fetchGetirOrders,
    fetchYemeksepetiOrders,
    handleUpdateOrderStatus,
    updateGetirOrderStatus,
    setSnackbar,
    setSelectCourierModalOpen,
  } = deps;

  const handleCourierSelect = useCallback(async (courierId: number, courierFirebaseUid: string, courierName: string) => {
    if (!selectedOrderId || !selectedOrder || !companyId) return;
    
    console.log('🛵 [CourierSelect] Kurye seçildi:', { courierId, courierFirebaseUid, courierName, orderId: selectedOrderId });
    
    try {
      const mapPaymentTypeToApi = (paymentType: PaymentType): string => {
        switch (paymentType) {
          case 'card': return 'POS';
          case 'online': return 'ONLINE';
          case 'cash':
          default: return 'CASH';
        }
      };

      // Trendyol siparişleri için önce platform-spesifik işlemler (Shipped durumu)
      if (selectedOrder.source === 'trendyol') {
        const trdPackageId = selectedOrder.trendyolPackageId;
        if (trdPackageId) {
          try {
            const { updateStatusInSql } = await import('../services/TrendyolYemekService');
            const trdOrderData = trendyolOrders.find(o => o.id === trdPackageId);
            await updateStatusInSql(trdPackageId, 'Shipped', {
              courierId,
              courierName,
              courierUid: courierFirebaseUid,
            }, trdOrderData);
            console.log('[CourierSelect] Trendyol SQL guncellendi (Shipped + kurye)');
          } catch (err) {
            console.warn('[CourierSelect] Trendyol SQL guncelleme hatasi:', err);
          }

          try {
            const shipResult = await updateTrendyolOrderStatus(trdPackageId, 'shipped');
            if (shipResult.success) {
              console.log('[CourierSelect] Trendyol API markShipped basarili');
            } else if (shipResult.statusMismatch) {
              // Self-heal: yerel state hook tarafından senkronlandı, kurye atama akışı kesilmesin.
              console.warn('[CourierSelect] Trendyol API markShipped — durum uyuşmazlığı:', shipResult.currentStatus);
            } else {
              console.warn('[CourierSelect] Trendyol API markShipped basarisiz:', shipResult.message);
            }
          } catch (err) {
            console.warn('[CourierSelect] Trendyol API markShipped hatasi:', err);
          }

          setTrendyolOrders(prev => prev.map(o =>
            o.id === trdPackageId ? { ...o, packageStatus: 'Shipped' } : o
          ));
        }
      }

      // orderSource ve platform ID'leri belirle
      const orderSource = selectedOrder.source === 'getir' ? 'getir'
        : selectedOrder.source === 'migros' ? 'migros'
        : selectedOrder.source === 'trendyol' ? 'trendyol'
        : selectedOrder.source === 'yemeksepeti' ? 'yemeksepeti'
        : 'package';

      const trdOrder = selectedOrder.source === 'trendyol'
        ? trendyolOrders.find(o => o.id === selectedOrder.trendyolPackageId)
        : null;

      const assignRequest = {
        packageOrderId: selectedOrderId,
        courierId,
        courierFirebaseUid,
        companyId,
        branchId: screenData?.branchId || null,
        orderNumber: selectedOrder.shortOrderNo || `PKT-${selectedOrderId}`,
        customerName: selectedOrder.customerName || 'Müşteri',
        customerPhone: selectedOrder.customerPhone || '',
        deliveryAddress: selectedOrder.customerAddress || '',
        totalAmount: selectedOrder.totalAmount || 0,
        itemCount: selectedOrder.itemCount || 0,
        notes: null,
        paymentMethod: mapPaymentTypeToApi(selectedOrder.paymentType),
        orderSource,
        getirOrderId: orderSource === 'getir' ? selectedOrderId : null,
        migrosOrderId: orderSource === 'migros' ? selectedOrderId : null,
        trendyolPackageId: orderSource === 'trendyol' ? selectedOrder.trendyolPackageId : null,
        trendyolSupplierId: orderSource === 'trendyol' && trdOrder ? String(trdOrder.supplierId) : null,
        trendyolDeliveryType: orderSource === 'trendyol' ? selectedOrder.trendyolDeliveryType : null,
        yemeksepetiOrderId: orderSource === 'yemeksepeti' ? selectedOrderId : null,
      };

      console.log('📤 [CourierSelect] CourierActiveOrders API çağrılıyor:', assignRequest);

      const assignResponse = await mainApi.post('/api/CourierActiveOrders/assign', assignRequest);
      const assignResult = assignResponse.data;

      if (assignResult.success) {
        console.log('✅ [CourierSelect] Kurye ataması başarılı:', assignResult);
        
        if (orderSource === 'getir') {
          console.log('🛵 [CourierSelect] Getir siparişi - kurye atandı, deliver bekliyor (TESLİM ET butonuyla)');
          
          try {
            await getirApi.post(`/api/getir-orders/${selectedOrderId}/assign-courier`, {
              courierId: courierId,
              courierName: courierName,
              courierFirebaseUid: courierFirebaseUid
            });
            console.log('✅ [CourierSelect] GetirOrders tablosu güncellendi (AssignedCourier)');
          } catch (err: any) {
            if (err?.response?.status === 401) {
              console.warn('⚠️ [CourierSelect] Token hatası (401) - token yenileme tetikleniyor...');
              refreshGetirToken();
            } else {
              console.warn('⚠️ [CourierSelect] GetirOrders AssignedCourier güncelleme hatası:', err);
            }
          }

          setGetirOrders(prev => prev.map(order => 
            order.id === selectedOrderId
              ? { ...order, assignedCourierId: courierId, assignedCourierName: courierName, assignedCourierFirebaseUid: courierFirebaseUid }
              : order
          ));
        } else if (orderSource === 'migros') {
          try {
            await migrosApi.post(`/api/migros-orders/${selectedOrderId}/assign-courier`, {
              CourierId: courierId,
              CourierName: courierName,
              CourierFirebaseUid: courierFirebaseUid
            });
            console.log('[CourierSelect] MigrosOrders tablosu guncellendi (AssignedCourier)');
          } catch (err) {
            console.warn('[CourierSelect] MigrosOrders AssignedCourier guncelleme hatasi:', err);
          }

          setMigrosOrders(prev => prev.map(order =>
            order.id === selectedOrderId
              ? { ...order, assignedCourierId: courierId, assignedCourierName: courierName, assignedCourierFirebaseUid: courierFirebaseUid }
              : order
          ));
        } else if (orderSource === 'yemeksepeti') {
          try {
            await yemeksepetiApi.post(`/api/ys-orders/${selectedOrderId}/assign-courier`, {
              CourierId: courierId,
              CourierName: courierName,
              CourierFirebaseUid: courierFirebaseUid
            });
            console.log('[CourierSelect] YemeksepetiOrders tablosu guncellendi (AssignedCourier)');
          } catch (err) {
            console.warn('[CourierSelect] YemeksepetiOrders AssignedCourier guncelleme hatasi:', err);
          }

          try {
            const pickupRes = (await yemeksepetiApi.post('/api/ys-orders/picked-up', {
              OrderId: selectedOrderId
            })).data;
            const pickupSuccess = pickupRes.Success ?? pickupRes.success;
            if (pickupSuccess) {
              console.log('[CourierSelect] YS picked-up otomatik guncellendi');
              setSnackbar({ open: true, message: `Kurye atandı ve kuryeye teslim edildi: ${courierName}`, severity: 'success' });
            }
          } catch (err) {
            console.warn('[CourierSelect] YS picked-up otomatik guncelleme hatasi:', err);
          }

          await fetchYemeksepetiOrders();
        } else if (orderSource === 'trendyol') {
          // Trendyol platform işlemleri zaten yukarıda yapıldı
        } else {
          await handleUpdateOrderStatus(selectedOrderId, 'ON_DELIVERY', courierFirebaseUid, courierName);
        }
        
        try {
          console.log('📱 [CourierSelect] Kuryeye bildirim gönderiliyor:', courierFirebaseUid);
          
          const notificationTitle = 'Yeni Sipariş Atandı 🚀';
          const notificationBody = `Sipariş #${selectedOrder.shortOrderNo || selectedOrderId} size atandı. Müşteri: ${selectedOrder.customerName || 'Müşteri'}. Lütfen siparişi kabul edin.`;
          
          // .NET notification API (tek çağrı: kaydet + FCM gönder)
          const notifResult = await NotificationService.sendNotification(
            companyId,
            [courierFirebaseUid],
            notificationTitle,
            notificationBody
          );
          
          if (notifResult.success) {
            console.log('✅ [CourierSelect] Kuryeye bildirim gönderildi:', notifResult.sentCount);
          } else {
            console.warn('⚠️ [CourierSelect] Kuryeye bildirim gönderilemedi:', notifResult.message);
          }
        } catch (notifError) {
          console.error('⚠️ [CourierSelect] Bildirim gönderme hatası (kritik değil):', notifError);
        }
        
        setSnackbar({ 
          open: true, 
          message: `✅ Sipariş ${courierName} kuryesine atandı`, 
          severity: 'success' 
        });
      } else {
        console.error('❌ [CourierSelect] Kurye ataması başarısız:', assignResult);
        setSnackbar({ 
          open: true, 
          message: `❌ Kurye ataması başarısız: ${assignResult.message || 'Bilinmeyen hata'}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('❌ [CourierSelect] Kurye atama hatası:', error);
      setSnackbar({ 
        open: true, 
        message: `❌ Kurye atama hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 
        severity: 'error' 
      });
    }
  }, [selectedOrderId, selectedOrder, companyId, screenData?.branchId, handleUpdateOrderStatus, updateGetirOrderStatus, fetchGetirOrders, setGetirOrders, setMigrosOrders, updateTrendyolOrderStatus, trendyolOrders, fetchYemeksepetiOrders]);

  return { handleCourierSelect };
};

export default useCourierHandlers;
